import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/shared/Primitives";
import { CheckCircle2, ShieldCheck, AlertTriangle, Sparkles, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useHandoverSession } from "../hooks/useHandoverSession";

// NOTE (Carer Essentials entitlement split, 2026-08-25): this checkout's
// plan_id ("carer_profile_monthly") is unchanged — same Stripe
// product/price as always — but it now grants the dedicated
// `carer_essentials` entitlement, not the legacy `carer_profile` one.
// Profile creation/visibility is entirely separate and free for every
// carer tier via `carer_free` (see care_profiles.py). See CHANGELOG for
// the full rationale + flagged follow-ups.
const INCLUDED = [
  "Private professional document storage",
  "Expiry and renewal tracking",
  "30-day and 7-day reminders",
  "Training and CPD record, with an annual CPD hours total",
  "Reference manager",
  "Work history record",
];

function AlreadyCoveredByPccNotice() {
  return (
    <div
      className="rounded-2xl border border-purple/30 bg-purple/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between"
      data-testid="membership-pro-carer-notice"
    >
      <div>
        <p className="font-semibold text-ink">Already a Pro Carer Office member?</p>
        <p className="mt-1 text-sm text-ink-muted leading-relaxed">
          Pro Carer Office already includes Carer Essentials — there's no need to purchase it separately.
        </p>
      </div>
      <Link
        to="/workspace/carer-essentials"
        className="btn-outline shrink-0 whitespace-nowrap"
        data-testid="membership-open-essentials-cta"
      >
        Open Carer Essentials
      </Link>
    </div>
  );
}

export default function MembershipCarerProfile() {
  const { ready, active, email: sessionEmail, entitlements } = useHandoverSession();
  const isProCarer = ready && active && entitlements.includes("pro_carer");
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", declarationAccepted: false });
  const [status, setStatus] = useState({ loading: false, error: "" });

  useEffect(() => {
    if (ready && active && sessionEmail) {
      setForm((f) => (f.email ? f : { ...f, email: sessionEmail }));
    }
  }, [ready, active, sessionEmail]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const canSubmit = form.email && form.firstName.trim() && form.lastName.trim() && form.declarationAccepted;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus({ loading: true, error: "" });
    try {
      const { data } = await api.post("/api/membership/checkout", {
        plan_id: "carer_profile_monthly",
        email: form.email,
        name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        declaration_accepted: form.declarationAccepted,
        origin_url: window.location.origin,
      });
      if (data.blocked) {
        setStatus({ loading: false, error: data.message, blockedReason: data.reason });
        return;
      }
      window.location.assign(data.checkout_url);
    } catch (err) {
      setStatus({
        loading: false,
        error: formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  };

  if (!ready) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="page-membership-carer-profile-loading">Loading…</div>;
  }

  if (isProCarer) {
    return (
      <div data-testid="page-membership-carer-profile">
        <PageHero
          eyebrow="Carer Essentials"
          title="You're already covered."
          description="Your Pro Carer Office membership already includes Carer Essentials — no separate purchase needed."
          accent="purple"
        />
        <section className="section">
          <div className="container-jcc max-w-xl">
            <div className="card-base p-8 text-center" data-testid="membership-already-pro-notice">
              <Sparkles className="w-10 h-10 mx-auto text-purple" />
              <h2 className="mt-4 font-display text-2xl font-bold">Already a Pro Carer Office member?</h2>
              <p className="mt-3 text-ink-muted leading-relaxed">
                Pro Carer Office already includes Carer Essentials — your private workspace for documents, renewals,
                training and CPD, references and work history.
              </p>
              <Link
                to="/workspace/carer-essentials"
                className="btn-purple mt-6 inline-flex"
                data-testid="membership-open-essentials-cta-pro"
              >
                Open Carer Essentials <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-membership-carer-profile">
      <PageHero
        eyebrow="Carer Essentials"
        title="Carer Essentials — £7 per month."
        description="Your professional care admin, all in one place. Keep your documents, renewals, training and CPD, references and work history organised in one private workspace."
        accent="purple"
      />
      <section className="section">
        <div className="container-jcc space-y-8">
          <AlreadyCoveredByPccNotice />
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 space-y-6">
              <div className="card-base p-6">
                <div className="flex items-center gap-2 text-purple font-semibold">
                  <ShieldCheck className="w-5 h-5" /> £7 / month · billed monthly
                </div>
                <p className="mt-2 text-xs font-semibold text-purple">Individual carer membership only</p>
                <p className="mt-2 text-sm text-ink-muted">
                  Secure checkout via Stripe. Cancel any time from your account — you'll keep access until the end of
                  the period you've already paid for.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <p className="text-sm font-semibold text-ink mb-3">What's included</p>
                <ul className="space-y-2.5">
                  {INCLUDED.map((it) => (
                    <li key={it} className="flex gap-2.5 text-sm text-ink-muted leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-purple" strokeWidth={1.75} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-ink-soft">
                Already have a Free Carer account? Signing up here upgrades it — you won't lose anything you've already
                set up.
              </p>
            </div>
            <form onSubmit={onSubmit} className="lg:col-span-3 card-base p-8 space-y-5" data-testid="membership-checkout-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">First name</label>
                  <input
                    required
                    value={form.firstName}
                    onChange={setField("firstName")}
                    data-testid="membership-checkout-first-name"
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-purple focus:ring-1 focus:ring-purple outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Last name</label>
                  <input
                    required
                    value={form.lastName}
                    onChange={setField("lastName")}
                    data-testid="membership-checkout-last-name"
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-purple focus:ring-1 focus:ring-purple outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Email address</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  data-testid="membership-checkout-email"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-purple focus:ring-1 focus:ring-purple outline-none"
                />
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={form.declarationAccepted}
                    onChange={(e) => setForm({ ...form, declarationAccepted: e.target.checked })}
                    data-testid="membership-checkout-declaration-checkbox"
                    className="mt-0.5 w-4 h-4"
                  />
                  <span>
                    I confirm that I am registering as an individual carer in my own name. I am not registering on
                    behalf of a care agency, care provider, recruitment business or other organisation.
                  </span>
                </label>
                <p className="mt-2 text-xs text-ink-muted">
                  Carer accounts are for individual carers only. Business and organisational accounts will be removed.
                </p>
              </div>
              {status.error && (
                <div
                  className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex gap-2"
                  data-testid="membership-checkout-error"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{status.error}</span>
                </div>
              )}
              <button
                disabled={status.loading || !canSubmit}
                className="btn-purple w-full md:w-auto disabled:opacity-50"
                data-testid="membership-checkout-submit"
              >
                {status.loading ? "Redirecting to secure checkout…" : "Continue to secure checkout"}
              </button>
              <p className="text-xs text-ink-soft">
                You'll be taken to Stripe's secure checkout page to complete payment. No card details are ever stored
                by Just Care Community.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

